import { RecordOf, hash } from 'immutable';
import { DateTime } from 'luxon';

import { crashReporterInterface, getLogger } from '../../utils/core';
import { coreActions } from '../core-actions';
import { datasetsActions } from '../datasets';
import { profileActions } from '../profile';
import * as userInfoActions from '../user-info/userInfoActions';

import { UnknownAction } from 'redux';
import * as actions from './authActions';
import { AuthSessionSources, AuthStoreProps, mkAuthStore } from './authTypes';

const log = getLogger('flux/auth/reducer');

function reducer(state: RecordOf<AuthStoreProps> = mkAuthStore(), action: UnknownAction): RecordOf<AuthStoreProps> {
  // TODO: Some of these `asyncDispatch`s are going to be gnarly to unwind and move to a saga so I am leaving this hack in for now
  const asyncDispatch = (action as any)?.asyncDispatch;
  switch (true) {
    case coreActions.sanitize.match(action):
      return state.merge({
        accessToken: hash(state.accessToken).toString(),
        refreshToken: hash(state.refreshToken).toString(),
        isSanitized: true,
      });
    case actions.getOAuthToken.match(action):
      return state.merge({
        isLoading: true,
        error: null,
      });
    case actions.getOAuthTokenSuccess.match(action):
      return state.merge({
        accessToken: action.payload.accessToken,
        accessTokenExpired: action.payload.accessTokenExpired,
        refreshToken: action.payload.refreshToken,
        refreshTokenExpired: action.payload.refreshTokenExpired,
        scope: action.payload.scope,
        surveyRequested: action.payload.surveyRequested,
        isLoading: false,
        error: null,
        source: AuthSessionSources.MASTER,
      });
    case actions.getOAuthTokenFailure.match(action):
      return state.merge({
        error: action.payload,
        isLoading: false,
      });
    case profileActions.getProfileSuccess.match(action): {
      const payload = action.payload;
      const profile = payload.resources && payload.resources.length && payload.resources[0];
      if (state.qcsId && profile && profile.userId !== state.qcsId) {
        assert(false, 'QCS ID changed in existing auth session - force logout');
        asyncDispatch(actions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }, { context: 'user id changed' }));
      }
      let authSession = state;
      if (!state.qcsId && profile) {
        authSession = authSession.merge({ qcsId: profile.userId });
        asyncDispatch(
          actions.authUpdated({ authSession, reload: false, prevAuthSession: state }, { context: 'getProfileSuccess' }),
        );
      }
      return authSession;
    }
    case actions.authSelectDataset.match(action): {
      const { dataset, reload = true } = action.payload;
      log.info('Auth: Selecting dataset', dataset, reload);
      assert(
        reload || !state.datasetId || !dataset?.id || state.datasetId === dataset?.id,
        'attempt to change valid datasetId to another one',
      );

      return reload
        ? state
        : state.merge({
            datasetId: dataset?.id ?? null,
            datasetPlatform: dataset?.platform ?? null,
            datasetSubscriptionTier: dataset?.subscriptionTier ?? null,
            datasetCreatedByClientId: dataset?.createdByClientId ?? null,
            datasetCreatedAt: dataset?.createdAt ?? null,
          }); // protect redux persist from overwriting datasetId
    }
    case datasetsActions.getDatasetsSuccess.match(action):
      if (state.datasetId) {
        const dataset = action.payload.resources.find((resource) => state.datasetId === resource.id);
        if (dataset) {
          const authSession = state.merge({
            datasetPlatform: dataset.platform,
            datasetSubscriptionTier: dataset.subscriptionTier,
            datasetCreatedByClientId: dataset.createdByClientId,
            datasetCreatedAt: dataset.createdAt,
          });
          asyncDispatch(
            actions.authUpdated(
              { authSession, reload: false, prevAuthSession: state },
              { context: 'getDatasetsSuccess' },
            ),
          );
          return authSession;
        }
      }
      return state;
    case actions.authLogin.match(action):
      return state.merge({
        accessToken: null,
        accessTokenExpired: null,
        refreshToken: null,
        refreshTokenExpired: null,
        error: null,
      });
    case actions.invalidateAccessToken.match(action):
      return state.merge({
        accessTokenExpired: DateTime.fromMillis(0).toISO(),
        error: null,
      });
    case actions.invalidateRefreshToken.match(action):
      crashReporterInterface.reportError(Error('Invalidate Refresh Token - force re auth'), {
        custom: {
          now: DateTime.utc().toISO(),
          datasetId: state?.datasetId,
          accessToken: state?.accessToken?.length,
          accessTokenExpired: state?.accessTokenExpired,
          refreshToken: state?.refreshToken?.length,
          refreshTokenExpired: state?.refreshTokenExpired,
          context: action.meta?.context,
        },
      });

      return state.merge({
        refreshTokenExpired: DateTime.fromMillis(0).toISO(),
        error: null,
      });
    case actions.updateAuth.match(action):
      return state.merge(action.payload);
    case userInfoActions.getUserInfoSuccess.match(action):
      return state.set('groups', action.payload?.resources?.[0]?.groups);
    case actions.exchangeRefreshTokenSuccess.match(action):
      return state.merge({
        accessToken: action.payload.accessToken || state.accessToken,
        accessTokenExpired: action.payload.accessTokenExpired || state.accessTokenExpired,
        source: AuthSessionSources.MASTER,
        scope: action.payload.scope || state.scope,
        surveyRequested: action.payload.surveyRequested,
      });
    case actions.exchangeRefreshTokenFailure.match(action):
      return state.set('error', action.payload);
    default:
      return state;
  }
}

export default reducer;
